// SMS segment math, ported from backend/api/services/sms.py so the composer
// shows the same number Twilio will bill. Two encodings:
//   GSM-7:  1–160 septets = 1 segment; beyond that 153/segment (concat header)
//   UCS-2:  any char outside GSM-7 forces it; 1–70 chars = 1 segment; then 67.
// GSM-7 extension chars (^ { } \ [ ~ ] | €, form-feed) cost TWO septets each.

const GSM7_BASIC = new Set(
  "@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ " +
    "!\"#¤%&'()*+,-./0123456789:;<=>?¡" +
    "ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§¿" +
    "abcdefghijklmnopqrstuvwxyzäöñüà"
);
const GSM7_EXTENSION = new Set("^{}\\[~]|€");

export interface SegmentInfo {
  encoding: "GSM-7" | "UCS-2";
  units: number; // septets (GSM-7) or chars (UCS-2)
  segments: number;
  /** units remaining before the NEXT segment starts */
  remainingInSegment: number;
}

export function smsSegmentInfo(body: string): SegmentInfo {
  const chars = Array.from(body);
  let gsm7 = true;
  let septets = 0;
  for (const ch of chars) {
    if (GSM7_BASIC.has(ch)) septets += 1;
    else if (GSM7_EXTENSION.has(ch)) septets += 2;
    else {
      gsm7 = false;
      break;
    }
  }

  if (gsm7) {
    const units = septets;
    const segments = units === 0 ? 0 : units <= 160 ? 1 : Math.ceil(units / 153);
    const capacity = segments <= 1 ? 160 : segments * 153;
    return {
      encoding: "GSM-7",
      units,
      segments,
      remainingInSegment: capacity - units,
    };
  }

  const units = chars.length;
  const segments = units === 0 ? 0 : units <= 70 ? 1 : Math.ceil(units / 67);
  const capacity = segments <= 1 ? 70 : segments * 67;
  return {
    encoding: "UCS-2",
    units,
    segments,
    remainingInSegment: capacity - units,
  };
}


/** Early GSM warning leaves room before the 160-unit first-segment limit. */
export function smsLengthWarning(info: SegmentInfo): boolean {
  return info.units > (info.encoding === "GSM-7" ? 153 : 70);
}
